import React, { useRef, useState } from "react";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { useTheme } from "../../ThemeProvider";
import { LucideUploadCloud } from "lucide-react";

export default function EnrichmentSplash({ onUploadClick, disabled = false }) {
  const { theme } = useTheme();
  const videoRef = useRef(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const handleViewDocs = () => {
    const docsUrl = `${process.env.REACT_APP_BASE_URL}/docs/contact-enrichment/fetch-contact-details-in-bulk/`;
    window.open(docsUrl, "_blank");
  };

  const handleContactSupport = () => {
    window.location.href =
      "mailto:support@primerole.com?cc=krishna@primerole.com&subject=I%20need%20help%20with%20CSV%20Enrichment&body=Please%20share%20your%20questions%20about%20CSV%20Enrichment%20";
  };

  return (
    <div className="bg-white dark:bg-gray-dark-50">
      {/* Header Section */}
      <div className="py-6">
        <div className="mb-3">
          <span className="text-[16px] font-semibold text-[#6941C6] dark:text-[#A78BFA]">Enrichment</span>
        </div>
        <h1 className="text-4xl font-semibold text-[#181D27] dark:text-white mb-5">
          Bulk CSV Enrichment Simplified
        </h1>
        <p className="text-xl text-[#535862] dark:text-gray-400 max-w-4xl font-normal">
          Enrich contacts and companies in bulk — fill missing fields, standardize data, and keep your
          records accurate using a single CSV upload.
        </p>
      </div>

      {/* Main Content */}
      <div className="pb-12 pt-1.5">
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <div className="bg-[#F5F5F5] dark:bg-[#121926] rounded-xl p-6">
                {/* Video Player */}
                <div className="relative mb-6 rounded-lg overflow-hidden bg-gray-900">
                  {videoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                      <div className="text-white text-sm">Loading video...</div>
                    </div>
                  )}
                  {videoError ? (
                    <div className="aspect-video flex items-center justify-center bg-gray-900 text-white text-sm">
                      <p>Unable to load video. Please check your connection.</p>
                    </div>
                  ) : (
                    <video
                      ref={videoRef}
                      className="w-full h-auto"
                      controls
                      controlsList="nodownload"
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={() => {
                        setVideoLoading(false);
                      }}
                      onLoadedData={() => {
                        setVideoLoading(false);
                      }}
                      onError={(e) => {
                        console.error('Video error:', e);
                        console.error('Error code:', videoRef.current?.error?.code);
                        console.error('Error message:', videoRef.current?.error?.message);
                        setVideoLoading(false);
                        setVideoError(true);
                      }}
                      onCanPlay={() => {
                        setVideoLoading(false);
                      }}
                    >
                      <source 
                        src={`${process.env.PUBLIC_URL}/images/icons/untitledui/video/BULKCSVENRICHMENT.mp4`} 
                        type="video/mp4" 
                      />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>

                <h2 className="text-lg font-semibold text-[#181D27] dark:text-white mb-1">
                  Bulk Enrichment for Every Team, at Any Scale.
                </h2>
                <p className="text-[#535862] text-base dark:text-white mb-4 leading-relaxed">
                  Whether you're enriching a few hundred records or millions, Bulk Enrichment provides
                  consistent, reliable data across contacts and companies — with full control over what gets
                  added.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={onUploadClick}
                    disabled={disabled}
                    className="bg-[#7F56D9] hover:bg-[#6941C6] text-white px-4 py-2 rounded-lg shadow-md flex items-center gap-2 font-semibold text-sm"
                  >
                    <LucideUploadCloud size={16} className="no-invert" />
                    Upload CSV
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#F5F5F5] dark:bg-[#121926] rounded-xl p-6">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-12">
                    <Image
                      src={
                        theme === "dark"
                          ? "/images/icons/untitledui/doc-icon-dark.svg"
                          : "/images/icons/untitledui/call-activity/doc.svg"
                      }
                      alt="Documentation"
                      width={48}
                      height={48}
                      className="no-invert"
                    />
                  </div>
                  <h3 className="font-semibold text-[#101828] dark:text-white mb-1">About Enrichment</h3>
                  <p className="text-sm text-[#475467] dark:text-white mb-4 leading-relaxed">
                  Read the Enrichment docs to explore features, setup, and best practices.
                  </p>
                  <button
                    onClick={handleViewDocs}
                    className="text-base text-[#6941C6] hover:text-[#7F56D9] dark:text-[#A78BFA] dark:hover:text-[#C4B5FD] font-semibold flex items-center gap-1"
                  >
                    View docs →
                  </button>
                </div>

                <div className="bg-[#F5F5F5] dark:bg-[#121926] rounded-xl p-6">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-12">
                    <Image
                      src={
                        theme === "dark"
                          ? "/images/icons/untitledui/headphone-icon-dark.svg"
                          : "/images/icons/untitledui/call-activity/head-phone.svg"
                      }
                      alt="Support"
                      width={48}
                      height={48}
                      className="no-invert"
                    />
                  </div>
                  <h3 className="font-semibold text-[#101828] dark:text-white mb-1">Need help?</h3>
                  <p className="text-sm text-[#475467] dark:text-white mb-4 leading-relaxed">
                  Need assistance with enrichment? We’re here to help you anytime.
                  </p>
                  <button
                    onClick={handleContactSupport}
                    className="text-base text-[#6941C6] hover:text-[#7F56D9] dark:text-[#A78BFA] dark:hover:text-[#C4B5FD] font-semibold flex items-center gap-1"
                  >
                    Contact support →
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-8">
              <div className="relative">
                <div className="rounded-xl overflow-hidden">
                  {theme === "dark" ? (
                    <Image
                      src="/images/icons/untitledui/Modern-screen-csv-dark.svg"
                      alt="CSV Enrichment Preview"
                      className="w-full h-auto no-invert"
                    />
                  ) : (
                    <Image
                      src="/images/icons/untitledui/Modern-screen-csv.svg"
                      alt="CSV Enrichment Preview"
                      className="w-full h-auto"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
